import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import SecondaryUserFormDialog from "./SecondaryUserFormDialog";
import {
    Trash2,
    UserPlus,
    Pencil,
    QrCode,
    CreditCard,
    Copy,
    ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

// `active` is undefined while the frontend is ahead of the backend deploy.
// For a dependant, `providerName` explains an inactive status: they're
// covered through that provider, so it's the provider who needs to pay.
function MembershipBadge({ active, providerName }) {
    if (active === undefined) return <span className="text-navy-300">—</span>;
    if (active)
        return (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                Active
            </Badge>
        );
    return (
        <>
            <Badge className="bg-navy-50 text-navy-500 border-navy-200 border">
                Not active
            </Badge>
            {providerName && (
                <div className="text-[11px] text-navy-400 mt-1">
                    Provider {providerName} has no active membership
                </div>
            )}
        </>
    );
}

export default function SecondaryUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogState, setDialogState] = useState(null);
    // dialogState shapes:
    //   null                                                     -> closed
    //   { mode: "create" }                                       -> plain add
    //   { mode: "create", presetProviderId, presetProviderName } -> add dependant
    //   { mode: "edit", row }                                    -> edit

    // QR / pay-link dialog state
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [payLink, setPayLink] = useState(null);
    const [payTarget, setPayTarget] = useState(null);
    const [payBusy, setPayBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/member/sub-users");
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    // The "self" row mirrors the primary member's own profile — it's
    // rendered as a distinct, non-editable row (see below) but isn't a
    // real, separately-billed family member, so it's excluded from the
    // family counts and from the sorted family-member rows.
    const selfRow = rows.find((r) => r.relationship === "self");
    const familyRows = rows.filter((r) => r.relationship !== "self");
    const dependants = familyRows.filter((r) => r.is_dependant);
    const adults = familyRows.filter((r) => !r.is_dependant);
    const providerNameById = new Map(familyRows.map((r) => [r.id, r.name]));
    // Like providerNameById but also resolves the self row, since a
    // dependant's provider can be the primary member themselves.
    const providerLabelById = new Map(
        rows.map((r) => [r.id, r.relationship === "self" ? `${r.name} (you)` : r.name])
    );
    // Eligible providers for a new/edited dependant: the primary member
    // themselves (their self row) plus any existing adult family member.
    // Mirrors the backend's own eligibility check in _resolve_provider.
    const providerOptions = [
        ...(selfRow ? [{ id: selfRow.id, name: `${selfRow.name} (you)` }] : []),
        ...adults.map((a) => ({ id: a.id, name: a.name })),
    ];

    async function remove(row) {
        if (!window.confirm(`Remove ${row.name}?`)) return;
        try {
            await api.delete(`/member/sub-users/${row.id}`);
            toast.success(`${row.name} removed`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function generatePayLink(row, kind = "membership") {
        setPayTarget(row);
        setPayBusy(true);
        setPayLink(null);
        setPayDialogOpen(true);
        try {
            const { data } = await api.post(
                `/member/sub-users/${row.id}/pay-link`,
                { kind, interval: "monthly" }
            );
            // Ensure the link is a full URL for the QR
            const abs = data.link.startsWith("http")
                ? data.link
                : `${window.location.origin}${data.link}`;
            setPayLink({ ...data, link: abs });
        } catch (err) {
            toast.error(formatApiError(err));
            setPayDialogOpen(false);
        } finally {
            setPayBusy(false);
        }
    }

    async function openSubPortal(row) {
        try {
            const { data } = await api.post(
                `/member/sub-users/${row.id}/billing-portal`,
                { return_url: window.location.href }
            );
            window.location.href = data.url;
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <>
            <PageHeader
                eyebrow="Family & dependants"
                title="Member profiles"
                description="Add your family to your Perx membership. Adults over 18 need their own subscription — dependants (under 18) redeem for free. Share a QR-code pay link to invite them to pay for themselves."
                action={
                    <Button
                        data-testid="add-secondary-member"
                        onClick={() => setDialogState({ mode: "create" })}
                        className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                    >
                        <UserPlus size={16} className="mr-2" /> Add member
                    </Button>
                }
                testid="secondary-members-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-8">
                <div className="grid sm:grid-cols-3 gap-5">
                    <SummaryCard label="Total" value={familyRows.length} />
                    <SummaryCard
                        label="Adults (paying)"
                        value={adults.length}
                        accent="gold"
                    />
                    <SummaryCard
                        label="Dependants (free)"
                        value={dependants.length}
                        accent="green"
                    />
                </div>

                <div className="rounded-2xl bg-white shadow-soft overflow-hidden">
                    <Table data-testid="secondary-members-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Name
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Contact
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Relationship
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Type
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Membership
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em] text-right">
                                    Actions
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {selfRow && (
                                <TableRow
                                    key={selfRow.id}
                                    data-testid={`secondary-member-row-${selfRow.id}`}
                                >
                                    <TableCell className="text-navy-700 font-medium">
                                        {selfRow.name}
                                        <div className="text-[11px] text-navy-400 mt-0.5">
                                            {selfRow.dob} · age {selfRow.age ?? "—"}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-navy-500">
                                        <div className="text-sm">{selfRow.email || "—"}</div>
                                        <div className="text-xs text-navy-400">
                                            {selfRow.phone || ""}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-navy-500">You</TableCell>
                                    <TableCell>
                                        <Badge className="bg-navy-50 text-navy-700 border-navy-200 border">
                                            Primary User
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <MembershipBadge active={selfRow.membership_active} />
                                    </TableCell>
                                    <TableCell className="text-right text-navy-300">—</TableCell>
                                </TableRow>
                            )}
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        className="text-center text-navy-500 py-10"
                                    >
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : familyRows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={6}
                                        className="text-center text-navy-500 py-16"
                                    >
                                        No secondary members yet. Click{" "}
                                        <strong>Add secondary member</strong> to invite a
                                        partner, adult family member or dependant.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                familyRows.map((r) => (
                                    <TableRow
                                        key={r.id}
                                        data-testid={`secondary-member-row-${r.id}`}
                                    >
                                        <TableCell className="text-navy-700 font-medium">
                                            {r.name}
                                            <div className="text-[11px] text-navy-400 mt-0.5">
                                                {r.dob} · age {r.age ?? "—"}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            <div className="text-sm">{r.email || "—"}</div>
                                            <div className="text-xs text-navy-400">{r.phone || ""}</div>
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            {r.relationship}
                                        </TableCell>
                                        <TableCell>
                                            {r.is_dependant ? (
                                                <>
                                                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                                                        Dependant · free
                                                    </Badge>
                                                    {r.primary_provider_id && (
                                                        <div className="text-[11px] text-navy-400 mt-1">
                                                            Depends on{" "}
                                                            {providerNameById.get(
                                                                r.primary_provider_id
                                                            ) || "—"}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <Badge className="bg-gold-500/10 text-gold-700 border-gold-200 border">
                                                    Adult · pays
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <MembershipBadge
                                                active={r.membership_active}
                                                providerName={
                                                    r.is_dependant
                                                        ? providerLabelById.get(
                                                              r.primary_provider_id
                                                          )
                                                        : null
                                                }
                                            />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    title="Edit"
                                                    data-testid={`secondary-edit-${r.id}`}
                                                    onClick={() =>
                                                        setDialogState({ mode: "edit", row: r })
                                                    }
                                                    className="h-8 rounded-full border-navy-200 text-navy-700 hover:bg-navy-50"
                                                >
                                                    <Pencil size={12} />
                                                </Button>
                                                {!r.is_dependant && (
                                                    <>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            title="Add dependant"
                                                            data-testid={`secondary-add-dependant-${r.id}`}
                                                            onClick={() =>
                                                                setDialogState({
                                                                    mode: "create",
                                                                    presetProviderId: r.id,
                                                                    presetProviderName: r.name,
                                                                })
                                                            }
                                                            className="h-8 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                        >
                                                            <UserPlus size={12} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            title="Send QR pay link"
                                                            data-testid={`secondary-qr-${r.id}`}
                                                            onClick={() => generatePayLink(r)}
                                                            className="h-8 rounded-full border-gold-300 text-gold-700 hover:bg-gold-50"
                                                        >
                                                            <QrCode size={12} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            title="Edit payment method"
                                                            data-testid={`secondary-portal-${r.id}`}
                                                            onClick={() => openSubPortal(r)}
                                                            className="h-8 rounded-full border-navy-200 text-navy-700 hover:bg-navy-50"
                                                        >
                                                            <CreditCard size={12} />
                                                        </Button>
                                                    </>
                                                )}
                                                <Button
                                                    data-testid={`secondary-remove-${r.id}`}
                                                    onClick={() => remove(r)}
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-8 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                                                    title="Remove"
                                                >
                                                    <Trash2 size={12} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
                <div className="text-xs text-navy-500">
                    Under-18 dependants can redeem member perks without a separate
                    subscription. Adults pay via their own card — send them a QR pay
                    link to activate.{" "}
                    <Link
                        to="/member/subscriptions"
                        className="text-gold-600 hover:underline"
                    >
                        View subscription →
                    </Link>
                </div>
            </div>

            <SecondaryUserFormDialog
                open={!!dialogState}
                onOpenChange={(o) => !o && setDialogState(null)}
                mode={dialogState?.mode}
                editingRow={dialogState?.mode === "edit" ? dialogState.row : null}
                presetProviderId={dialogState?.presetProviderId ?? null}
                presetProviderName={dialogState?.presetProviderName ?? null}
                providerOptions={providerOptions}
                onSaved={() => {
                    setDialogState(null);
                    load();
                }}
            />

            {/* QR pay-link dialog */}
            <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
                <DialogContent
                    data-testid="secondary-qr-dialog"
                    className="bg-white border-navy-100 max-w-md"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-navy-700">
                            Share pay link with {payTarget?.name}
                        </DialogTitle>
                        <DialogDescription className="text-navy-500">
                            They scan this QR (or open the link) to complete their
                            subscription on their own card. Link is valid for 7 days,
                            single-use.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-4 py-4">
                        {payBusy || !payLink ? (
                            <div className="text-sm text-navy-500 py-16">
                                Generating secure link…
                            </div>
                        ) : (
                            <>
                                <div
                                    data-testid="secondary-qr-canvas"
                                    className="rounded-2xl bg-cream p-4 border border-gold-200"
                                >
                                    <QRCodeCanvas
                                        value={payLink.link}
                                        size={220}
                                        bgColor="#FAFAF7"
                                        fgColor="#001c50"
                                        includeMargin={false}
                                    />
                                </div>
                                <div className="w-full">
                                    <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                        Or send this link
                                    </Label>
                                    <div className="flex mt-1 gap-2">
                                        <Input
                                            readOnly
                                            data-testid="secondary-qr-link"
                                            value={payLink.link}
                                            className="text-xs font-mono"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            data-testid="secondary-qr-copy"
                                            onClick={() => {
                                                navigator.clipboard.writeText(payLink.link);
                                                toast.success("Link copied");
                                            }}
                                            className="h-10 rounded-md border-navy-200"
                                        >
                                            <Copy size={14} />
                                        </Button>
                                    </div>
                                </div>
                                <a
                                    href={payLink.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="secondary-qr-open"
                                    className="text-xs text-navy-500 hover:text-gold-600 inline-flex items-center gap-1"
                                >
                                    Preview payment page{" "}
                                    <ExternalLink size={11} />
                                </a>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SummaryCard({ label, value, accent = "navy" }) {
    const tone =
        accent === "gold"
            ? "text-gold-700"
            : accent === "green"
              ? "text-emerald-700"
              : "text-navy-700";
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-navy-500 mb-2">
                {label}
            </div>
            <div className={`font-display text-4xl ${tone}`}>{value}</div>
        </div>
    );
}
